import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Transition } from "@headlessui/react";
import { AcademicCapIcon } from "@heroicons/react/24/solid";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import useAuth from "../../../utils/hooks/useAuth";
import { UserContext } from "../../../utils/contexts/UserContext";
import { getApiErrorMessage } from "../../../utils/apiError";
import { getHomePage } from "../../../utils/permissions";

// Outlined field; the label rests inside it and floats onto the border (notch) on focus or when filled.
const fieldClass =
  "peer block h-14 w-full rounded-xl border-slate-300 bg-white px-4 text-base text-slate-900 transition duration-200 ease-out hover:border-slate-400 focus:border-blue-600 focus:ring-blue-600 focus:shadow-[0_0_0_4px_rgb(37_99_235/0.12)]";
const labelClass =
  "pointer-events-none absolute left-3 top-0 origin-left translate-y-4 rounded bg-white px-1 text-base leading-6 text-slate-500 transition duration-200 ease-out motion-reduce:transition-colors peer-valid:-translate-y-3 peer-valid:scale-75 peer-valid:text-slate-600 peer-autofill:-translate-y-3 peer-autofill:scale-75 peer-focus:-translate-y-3 peer-focus:scale-75 peer-focus:text-blue-600";

// Height reveal (grid rows 0fr -> 1fr) plus fade for the error alert.
const collapsed = "grid-rows-[0fr] opacity-0";
const expanded = "grid-rows-[1fr] opacity-100";

export default function Login() {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { register, handleSubmit } = useForm();

  const { user } = useContext(UserContext);
  const { loginUser, error } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  // No response at all (offline, server unreachable): say so instead of the generic login failure.
  const errorFallbackKey =
    axios.isAxiosError(error) && !error.response
      ? "exam_taking.validation_network_error"
      : "signup.login_failed";
  // Once the user is set the page redirects; an earlier attempt's error must not come back meanwhile.
  const showError = Boolean(error) && !loading && !user;
  const errorMessage = getApiErrorMessage(t, error, errorFallbackKey);

  const onSubmit = async (data) => {
    setLoading(true);
    await loginUser(data).finally(() => setLoading(false));
  };

  // The page that sent the user here (a folder link, an invite) opens after the login, else the home page.
  const from = location.state?.from;
  const returnTo = from?.pathname && from.pathname !== "/login" ? `${from.pathname}${from.search || ""}` : null;

  useEffect(() => {
    if (user?.uuid && user?.perms) {
      navigate(returnTo || getHomePage(user), { replace: true });
    }
  }, [user, navigate, returnTo]);

  return (
    <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-card ring-1 ring-slate-900/5 motion-safe:animate-rise lg:max-w-lg">
      <header className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-600 px-6 pb-8 pt-7 text-white sm:px-10 sm:pb-9 sm:pt-10">
        <svg
          aria-hidden="true"
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="0.6"
          className="absolute -right-24 -top-24 h-80 w-80 text-white/15"
        >
          <circle cx="50" cy="50" r="14" />
          <circle cx="50" cy="50" r="24" />
          <circle cx="50" cy="50" r="34" />
          <circle cx="50" cy="50" r="44" />
        </svg>
        <span className="relative grid h-12 w-12 place-items-center rounded-2xl bg-white text-blue-600 shadow-lg shadow-indigo-950/20 motion-safe:animate-fade-up-1">
          <AcademicCapIcon className="h-7 w-7" aria-hidden="true" />
        </span>
        <h1 className="relative mt-5 text-2xl font-bold tracking-tight sm:text-3xl motion-safe:animate-fade-up-2">
          Question Bank
        </h1>
        <p className="relative mt-1.5 text-sm text-blue-50 sm:text-base motion-safe:animate-fade-up-3">
          {t("signup.login_tagline")}
        </p>
      </header>

      <div className="px-6 py-7 sm:px-10 sm:py-9">
        <Transition
          show={showError}
          className="grid"
          enter="transition-all duration-300 ease-out motion-reduce:transition-opacity"
          enterFrom={collapsed}
          enterTo={expanded}
          leave="transition-all duration-200 ease-in motion-reduce:transition-opacity"
          leaveFrom={expanded}
          leaveTo={collapsed}
        >
          <div className="overflow-hidden">
            <div
              role="alert"
              className="mb-6 flex gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              <ExclamationCircleIcon className="h-5 w-5 shrink-0 text-red-500" aria-hidden="true" />
              <p>{errorMessage}</p>
            </div>
          </div>
        </Transition>

        <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
          <div className="relative">
            <input
              id="username"
              required
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              className={fieldClass}
              {...register("username", {
                required: true,
                maxLength: 64,
              })}
            />
            <label htmlFor="username" className={labelClass}>
              {t("signup.username")}
            </label>
          </div>

          <div className="relative">
            <input
              id="password"
              type={isVisible ? "text" : "password"}
              required
              autoComplete="current-password"
              className={`${fieldClass} pr-14`}
              {...register("password", {
                required: true,
                maxLength: 64,
              })}
            />
            <label htmlFor="password" className={labelClass}>
              {t("signup.password")}
            </label>
            <button
              type="button"
              aria-controls="password"
              aria-label={t(isVisible ? "signup.hide_password" : "signup.show_password")}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setIsVisible((visible) => !visible)}
              className="absolute inset-y-0 right-2 my-auto grid h-10 w-10 place-items-center rounded-lg text-slate-500 transition duration-200 hover:bg-slate-100 hover:text-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 active:scale-90"
            >
              <EyeSlashIcon
                aria-hidden="true"
                className={`col-start-1 row-start-1 h-5 w-5 transition duration-200 ${isVisible ? "scale-50 opacity-0" : ""}`}
              />
              <EyeIcon
                aria-hidden="true"
                className={`col-start-1 row-start-1 h-5 w-5 transition duration-200 ${isVisible ? "" : "scale-50 opacity-0"}`}
              />
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="relative flex h-12 w-full items-center justify-center rounded-xl bg-blue-600 px-5 text-base font-semibold text-white shadow-lg shadow-blue-600/25 transition duration-200 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 enabled:hover:bg-blue-700 enabled:hover:shadow-blue-600/40 enabled:active:scale-[0.98] disabled:cursor-wait motion-safe:enabled:hover:-translate-y-0.5"
          >
            <span className={`transition duration-200 ${loading ? "scale-95 opacity-0" : ""}`}>
              {t("signup.login")}
            </span>
            {loading && (
              <span aria-hidden="true" className="absolute inset-0 grid place-items-center motion-safe:animate-fade-in">
                <svg className="h-6 w-6 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" className="opacity-30" />
                  <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </span>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
